import {
  CreateOperatingCostInvoiceLineItemV1Dto,
  OperatingCostInvoiceLineItemV1Dto,
  OperatingCostInvoicesService,
  PatchOperatingCostInvoiceLineItemV1Dto,
} from '@/openapi';

import type { OperatingCostInvoiceLineItemSimple } from '../operatingCostInvoiceTypes';

export function useOperatingCostInvoiceLines() {
  const fetchOperatingCostInvoiceLine = async (invoiceId: string, id: string) => {
    return await OperatingCostInvoicesService.operatingCostInvoiceLineItemControllerV1FindOneV1(
      invoiceId,
      id,
    );
  };

  const createOperatingCostInvoiceLine = async (
    invoiceId: string,
    data: CreateOperatingCostInvoiceLineItemV1Dto,
  ) => {
    await OperatingCostInvoicesService.operatingCostInvoiceLineItemControllerV1CreateV1(
      invoiceId,
      data,
    );
  };

  const updateOperatingCostInvoiceLine = async (
    invoiceId: string,
    id: string,
    data: PatchOperatingCostInvoiceLineItemV1Dto,
  ) => {
    await OperatingCostInvoicesService.operatingCostInvoiceLineItemControllerV1UpdatePartialV1(
      invoiceId,
      id,
      data,
    );
  };

  const deleteOperatingCostInvoiceLine = async (invoiceId: string, id: string) => {
    await OperatingCostInvoicesService.operatingCostInvoiceLineItemControllerV1RemoveOneV1(
      invoiceId,
      id,
    );
  };

  const deleteOperatingCostInvoiceLines = async (invoiceId: string, ids: string[]) => {
    await OperatingCostInvoicesService.operatingCostInvoiceLineItemControllerV1RemoveManyV1(
      invoiceId,
      {
        values: ids,
      },
    );
  };

  return {
    fetchOperatingCostInvoiceLine,
    createOperatingCostInvoiceLine,
    updateOperatingCostInvoiceLine,
    deleteOperatingCostInvoiceLine,
    deleteOperatingCostInvoiceLines,
  };
}

export function useOperatingCostInvoiceLinesConverter() {
  const convertOperatingCostInvoiceLineItemDtoToOperatingCostInvoiceLineItemSimple = (
    data: OperatingCostInvoiceLineItemV1Dto,
  ): OperatingCostInvoiceLineItemSimple => {
    const {
      createdAt: _createdAt,
      modifiedAt: _modifiedAt,
      area: _area,
      position: _position,
      invoiceId: _invoiceId,
      sfmPosition: _sfmPosition,
      ...res
    } = data;
    return {
      ...res,
      sfmPositionId: data.sfmPosition?.id,
      sfmPositionName: data.sfmPosition?.name,
    };
  };

  const convertOperatingCostInvoiceLineItemDtoListToOperatingCostInvoiceLineItemSimpleList = (
    data: OperatingCostInvoiceLineItemV1Dto[],
  ): OperatingCostInvoiceLineItemSimple[] => {
    return data.map(convertOperatingCostInvoiceLineItemDtoToOperatingCostInvoiceLineItemSimple);
  };

  const convertOperatingCostInvoiceLineItemSimpleToCreateOperatingCostInvoiceLineItemDto = (
    data: OperatingCostInvoiceLineItemSimple,
  ): CreateOperatingCostInvoiceLineItemV1Dto => {
    const {
      version: _version,
      id: _id,
      invoiceItemSource: _invoiceItemSource,
      sfmPositionName: _sfmPositionName,
      status,
      ...res
    } = data;
    return { ...res, status: status ?? CreateOperatingCostInvoiceLineItemV1Dto.status.UNCLEAR };
  };

  const convertOperatingCostInvoiceLineItemSimpleToPatchOperatingCostInvoiceLineItemDto = (
    data: OperatingCostInvoiceLineItemSimple,
  ): PatchOperatingCostInvoiceLineItemV1Dto => {
    const {
      id: _id,
      invoiceItemSource: _invoiceItemSource,
      sfmPositionName: _sfmPositionName,
      ...res
    } = data;
    return { ...res };
  };

  return {
    convertOperatingCostInvoiceLineItemDtoToOperatingCostInvoiceLineItemSimple,
    convertOperatingCostInvoiceLineItemDtoListToOperatingCostInvoiceLineItemSimpleList,
    convertOperatingCostInvoiceLineItemSimpleToCreateOperatingCostInvoiceLineItemDto,
    convertOperatingCostInvoiceLineItemSimpleToPatchOperatingCostInvoiceLineItemDto,
  };
}
